import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { X, Copy, Check, Share2 } from 'lucide-react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Share the site: a QR code pointing at the home page, plus copy-link and
// the native share sheet where the browser offers one.
const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = window.location.origin;

  useEffect(() => {
    if (!isOpen) return;
    setCopied(false);
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — the URL is shown
      // below the QR code so it can still be copied by hand
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: '4HVNSK8', url: shareUrl });
    } catch {
      // User dismissed the share sheet — nothing to do
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Share this site"
    >
      <div
        className="bg-white text-black w-full max-w-sm p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <h2 className="font-heading font-bold text-sm tracking-[0.3em] uppercase text-center mb-2">
          Share the archive
        </h2>
        <p className="font-mono text-[11px] text-gray-500 tracking-widest text-center mb-6">
          Scan to visit 4HVNSK8.
        </p>

        <div className="flex justify-center mb-6">
          <div className="bg-white p-3 border border-gray-200">
            <QRCode value={shareUrl} size={192} aria-label={`QR code linking to ${shareUrl}`} />
          </div>
        </div>

        <p className="font-mono text-xs text-gray-500 text-center break-all mb-6">{shareUrl}</p>

        <div className="flex flex-col gap-2">
          <button
            onClick={handleCopy}
            className="w-full border border-black py-3 font-heading font-bold text-xs tracking-widest uppercase hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-2"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Link copied' : 'Copy link'}
          </button>
          {typeof navigator.share === 'function' && (
            <button
              onClick={handleNativeShare}
              className="w-full bg-black text-white py-3 font-heading font-bold text-xs tracking-widest uppercase hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
            >
              <Share2 size={14} /> Share…
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
